import React, { useMemo, useState } from 'react';
import './ModuleRadar.css';
import { useIsPhone } from '../../hooks/useMediaQuery';

const TAU = Math.PI * 2;
const START_ANGLE = -Math.PI / 2;

const COLORS = {
  primary: '#5C3BD1',
  primarySoft: 'rgba(92,59,209,0.18)',
  primaryGhostStroke: 'rgba(92,59,209,0.35)',
  primaryGhostFill: 'rgba(92,59,209,0.08)',
  grid: '#E5E7EB',
  gridStrong: '#D1D5DB',
  text: '#1F2937',
  textMuted: '#6B7280',
  sector: 'rgba(92,59,209,0.09)',
};

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

const toCartesian = (cx, cy, radius, angle) => ({
  x: cx + Math.cos(angle) * radius,
  y: cy + Math.sin(angle) * radius,
});

const toPolygonPoints = (points) => points.map((point) => `${point.x},${point.y}`).join(' ');

const truncateLabel = (label, maxLength = 14) => {
  if (!label) return '';
  if (label.length <= maxLength) return label;
  return `${label.slice(0, maxLength - 1)}...`;
};

const wrapLabel = (label, maxCharsPerLine = 18, maxLines = 2) => {
  if (!label) return [''];
  const words = String(label).split(/\s+/).filter(Boolean);
  if (!words.length) return [''];

  const lines = [];
  let current = '';

  for (const word of words) {
    const next = current ? `${current} ${word}` : word;
    if (next.length <= maxCharsPerLine) {
      current = next;
      continue;
    }

    if (current) {
      lines.push(current);
      current = word;
    } else {
      lines.push(word.length > maxCharsPerLine ? truncateLabel(word, maxCharsPerLine) : word);
      current = '';
    }

    if (lines.length >= maxLines) break;
  }

  if (lines.length < maxLines && current) {
    lines.push(current);
  }

  if (lines.length > maxLines) {
    return lines.slice(0, maxLines);
  }

  if (lines.length === maxLines && words.join(' ').length > lines.join(' ').length) {
    lines[maxLines - 1] = truncateLabel(lines[maxLines - 1], Math.max(4, maxCharsPerLine - 1));
  }

  return lines;
};

const buildSectorPath = (cx, cy, outerRadius, startAngle, endAngle) => {
  const start = toCartesian(cx, cy, outerRadius, startAngle);
  const end = toCartesian(cx, cy, outerRadius, endAngle);
  const delta = ((endAngle - startAngle + TAU) % TAU) || TAU;
  const largeArcFlag = delta > Math.PI ? 1 : 0;
  return `M ${cx} ${cy} L ${start.x} ${start.y} A ${outerRadius} ${outerRadius} 0 ${largeArcFlag} 1 ${end.x} ${end.y} Z`;
};

/** Outer web radius as a fraction of layout `size` (padding is separate). ~0.31 original; higher = larger radar in the same box. */
const OUTER_RADIUS_RATIO = 0.39;
/** Gap from web edge to label anchor (user units). */
const LABEL_OUTSET = 20;

function ModuleRadar({
  modules,
  previousModules = [],
  snapshotLabel = '',
  size = 420,
  onModuleSelect,
}) {
  const [hoveredModuleId, setHoveredModuleId] = useState(null);
  const isPhone = useIsPhone();
  const safeModules = Array.isArray(modules) ? modules : [];
  const previousById = useMemo(
    () => new Map((previousModules || []).map((module) => [String(module.id), module])),
    [previousModules],
  );

  const viewPadding = 92;
  const viewSize = size + 2 * viewPadding;
  const center = viewPadding + size / 2;
  const outerRadius = size * OUTER_RADIUS_RATIO;
  const ringScales = [0.2, 0.4, 0.6, 0.8, 1];

  const points = useMemo(() => {
    if (!safeModules.length) return [];
    return safeModules.map((module, index) => {
      const angle = START_ANGLE + (index * TAU) / safeModules.length;
      const ratio = clamp((Number(module.value) || 0) / 100, 0, 1);
      const cos = Math.cos(angle);
      const sin = Math.sin(angle);
      // Push labels further out on side axes so long titles can use surrounding whitespace.
      const outwardBoost = Math.abs(cos) > 0.55 ? 40 : Math.abs(sin) > 0.7 ? 16 : 24;
      const valuePoint = toCartesian(center, center, outerRadius * ratio, angle);
      const outerPoint = toCartesian(center, center, outerRadius, angle);
      const labelPoint = toCartesian(center, center, outerRadius + LABEL_OUTSET + outwardBoost, angle);
      return {
        ...module,
        angle,
        cos,
        sin,
        valuePoint,
        outerPoint,
        labelPoint,
      };
    });
  }, [safeModules, center, outerRadius]);

  const previousPoints = useMemo(() => {
    if (!points.length) return [];
    return points.map((point) => {
      const previous = previousById.get(String(point.id));
      const previousRatio = clamp((Number(previous?.value) || 0) / 100, 0, 1);
      return toCartesian(center, center, outerRadius * previousRatio, point.angle);
    });
  }, [points, previousById, center, outerRadius]);

  const currentPolygon = toPolygonPoints(points.map((point) => point.valuePoint));
  const previousPolygon = toPolygonPoints(previousPoints);
  const hasComparison = previousModules.length > 0;
  const hoveredPoint = points.find((point) => String(point.id) === String(hoveredModuleId));
  const angleStep = points.length > 0 ? TAU / points.length : 0;

  const tooltip = useMemo(() => {
    if (!hoveredPoint) return null;
    const rawX = hoveredPoint.valuePoint.x + (Math.cos(hoveredPoint.angle) >= 0 ? 12 : -148);
    const rawY = hoveredPoint.valuePoint.y - 56;
    const tooltipWidth = 136;
    const tooltipHeight = 50;
    return {
      x: clamp(rawX, 8, viewSize - tooltipWidth - 8),
      y: clamp(rawY, 8, viewSize - tooltipHeight - 8),
      width: tooltipWidth,
      height: tooltipHeight,
      label: hoveredPoint.label,
      value: clamp(Number(hoveredPoint.value) || 0, 0, 100),
    };
  }, [hoveredPoint, viewSize]);

  if (!points.length) return null;

  return (
    <div className="mb-4 rounded-xl border border-gray-200 bg-[#FAF8FF] p-4 shadow-sm">
      <div className="mb-2 flex items-center justify-between gap-2">
        <h2 className="text-sm font-semibold text-gray-900">Module Radar</h2>
        <p className="text-xs text-gray-500">Hover modules to see average score</p>
      </div>

      {hasComparison && snapshotLabel ? (
        <div className="mb-3 flex flex-wrap items-center gap-4 text-xs text-gray-500">
          <span className="flex items-center gap-1.5">
            <svg width="20" height="8" aria-hidden="true">
              <line x1="0" y1="4" x2="20" y2="4" stroke={COLORS.primary} strokeWidth="2" />
            </svg>
            Today
          </span>
          <span className="flex items-center gap-1.5">
            <svg width="20" height="8" aria-hidden="true">
              <line
                x1="0"
                y1="4"
                x2="20"
                y2="4"
                stroke={COLORS.primaryGhostStroke}
                strokeWidth="1.5"
                strokeDasharray="4 4"
              />
            </svg>
            End of {snapshotLabel}
          </span>
        </div>
      ) : null}

      <svg
        viewBox={`0 0 ${viewSize} ${viewSize}`}
        className="module-radar-svg mx-auto h-auto w-full max-w-[540px] shrink-0"
        role="img"
        aria-label="Module performance radar chart"
      >
        <defs>
          <linearGradient id="module-radar-fill" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="rgba(92,59,209,0.26)" />
            <stop offset="100%" stopColor="rgba(92,59,209,0.10)" />
          </linearGradient>
          <filter id="module-radar-glow" x="-200%" y="-200%" width="400%" height="400%">
            <feDropShadow dx="0" dy="0" stdDeviation="2.8" floodColor={COLORS.primary} floodOpacity="0.45" />
          </filter>
        </defs>

        {ringScales.map((scale, index) => (
          <circle
            key={`ring-${scale}`}
            cx={center}
            cy={center}
            r={outerRadius * scale}
            fill="none"
            stroke={index === ringScales.length - 1 ? COLORS.gridStrong : COLORS.grid}
            strokeWidth={index === ringScales.length - 1 ? 1.4 : 1}
          />
        ))}

        {points.map((point) => (
          <line
            key={`axis-${point.id}`}
            x1={center}
            y1={center}
            x2={point.outerPoint.x}
            y2={point.outerPoint.y}
            stroke={hoveredModuleId === point.id ? COLORS.primary : COLORS.grid}
            strokeWidth={hoveredModuleId === point.id ? 1.8 : 1}
            style={{ transition: 'all 180ms ease-out' }}
          />
        ))}

        {points.map((point) => (
          <path
            key={`sector-${point.id}`}
            d={buildSectorPath(center, center, outerRadius, point.angle - angleStep / 2, point.angle + angleStep / 2)}
            fill={hoveredModuleId === point.id ? COLORS.sector : 'transparent'}
            onMouseEnter={() => setHoveredModuleId(point.id)}
            onMouseLeave={() => setHoveredModuleId(null)}
            onFocus={() => setHoveredModuleId(point.id)}
            onBlur={() => setHoveredModuleId(null)}
            onClick={() => onModuleSelect?.(point.id)}
            tabIndex={0}
            role="button"
            aria-label={`${point.label} average ${Math.round((Number(point.value) || 0) * 10) / 10} percent`}
            style={{ cursor: 'pointer', transition: 'fill 180ms ease-out' }}
          />
        ))}

        {hasComparison ? (
          <polygon
            points={previousPolygon}
            fill={COLORS.primaryGhostFill}
            stroke={COLORS.primaryGhostStroke}
            strokeDasharray="4 4"
            strokeWidth="1.5"
          />
        ) : null}

        <polygon
          points={currentPolygon}
          fill="url(#module-radar-fill)"
          stroke={COLORS.primary}
          strokeWidth="2.2"
          style={{ transition: 'all 180ms ease-out' }}
        />

        {points.map((point) => {
          const isHovered = hoveredModuleId === point.id;
          return (
            <g key={`point-${point.id}`}>
              {isHovered ? (
                <circle
                  cx={point.valuePoint.x}
                  cy={point.valuePoint.y}
                  r="7"
                  className="module-radar-pulse"
                />
              ) : null}
              <circle
                cx={point.valuePoint.x}
                cy={point.valuePoint.y}
                r={isHovered ? 5 : 3.5}
                fill={COLORS.primary}
                filter={isHovered ? 'url(#module-radar-glow)' : undefined}
                style={{ transition: 'all 180ms ease-out' }}
              />
            </g>
          );
        })}

        {points.map((point) => {
          const nearRight = point.cos > 0.35;
          const nearLeft = point.cos < -0.35;
          const anchor = nearRight ? 'start' : nearLeft ? 'end' : 'middle';
          const verticalNudge = point.sin > 0.55 ? 10 : point.sin < -0.55 ? -6 : 0;
          const horizontalNudge = nearRight ? 10 : nearLeft ? -10 : 0;
          const labelX = clamp(point.labelPoint.x + horizontalNudge, 12, viewSize - 12);
          const labelY = clamp(point.labelPoint.y + verticalNudge, 12, viewSize - 12);
          // The SVG scales down ~0.56x on a phone, so draw labels larger there to stay legible.
          const maxCharsPerLine = isPhone ? (nearRight || nearLeft ? 15 : 12) : nearRight || nearLeft ? 25 : 18;
          const lines = wrapLabel(point.label, maxCharsPerLine, 2);
          const lineHeight = isPhone ? 17 : 11;
          const startDy = lines.length > 1 ? -lineHeight / 2 : 0;
          const isHovered = hoveredModuleId === point.id;

          return (
            <text
              key={`label-${point.id}`}
              x={labelX}
              y={labelY}
              textAnchor={anchor}
              dominantBaseline="middle"
              fill={isHovered ? COLORS.text : COLORS.textMuted}
              fontSize={isPhone ? 16 : 10}
              style={{
                cursor: 'pointer',
                transition: 'fill 180ms ease-out, font-size 180ms ease-out',
                fontWeight: isHovered ? 600 : 500,
              }}
              onMouseEnter={() => setHoveredModuleId(point.id)}
              onMouseLeave={() => setHoveredModuleId(null)}
              onClick={() => {
                // Touch has no hover: a tap also shows the score tooltip.
                setHoveredModuleId(point.id);
                onModuleSelect?.(point.id);
              }}
            >
              {lines.map((line, index) => (
                <tspan key={`${point.id}-line-${index}`} x={labelX} dy={index === 0 ? startDy : lineHeight}>
                  {line}
                </tspan>
              ))}
              <title>{point.label}</title>
            </text>
          );
        })}

        {tooltip ? (
          <g transform={`translate(${tooltip.x}, ${tooltip.y})`} style={{ pointerEvents: 'none' }}>
            <rect
              x="0"
              y="0"
              width={tooltip.width}
              height={tooltip.height}
              rx="10"
              fill="white"
              stroke="#D1D5DB"
              strokeWidth="1"
              filter="url(#module-radar-glow)"
            />
            <text x="10" y="18" fill={COLORS.text} fontSize="10" fontWeight="600">
              {truncateLabel(tooltip.label, 18)}
            </text>
            <text x="10" y="34" fill={COLORS.textMuted} fontSize="9">
              Average score
            </text>
            <text x={tooltip.width - 10} y="34" textAnchor="end" fill={COLORS.primary} fontSize="11" fontWeight="700">
              {tooltip.value.toFixed(1)}%
            </text>
          </g>
        ) : null}
      </svg>
    </div>
  );
}

export default ModuleRadar;
